/**
 * Sistema de Tipos para RAE Project Management
 * Definiciones TypeScript para el Sistema de Seguimiento de Ensamblaje y Proyectos
 */

// ==================== USER / AUTH TYPES ====================

export type UserRole = 'PROJECT_MANAGER' | 'TECHNICIAN' | 'HELPER' | 'ELECTRICIAN' | 'MECHANIC';

// ==================== SIDEBAR SECTION VISIBILITY ====================

export type SidebarSection =
  | 'dashboard'
  | 'workspace'
  | 'my-tasks'
  | 'team'
  | 'reports'
  | 'planning'
  | 'settings'
  | 'calendar';



// ==================== PERMISSION SYSTEM ====================

export type Permission =
  | 'dashboard'
  | 'workspace'
  | 'workspace_edit'
  | 'my-tasks'
  | 'team'
  | 'team_manage'
  | 'reports'
  | 'settings'
  | 'calendar';

export const RolePermissions: Record<UserRole, Permission[]> = {
  PROJECT_MANAGER: [
    'dashboard', 'workspace', 'workspace_edit',
    'my-tasks', 'team', 'team_manage', 'reports', 'settings', 'calendar',
  ],
  TECHNICIAN: ['my-tasks', 'calendar'],
  HELPER: ['my-tasks', 'calendar'],
  ELECTRICIAN: ['my-tasks', 'calendar'],
  MECHANIC: ['my-tasks', 'calendar'],
};

export const UserRoleLabels: Record<UserRole, string> = {
  PROJECT_MANAGER: 'Project Manager',
  TECHNICIAN: 'Technician',
  HELPER: 'Helper',
  ELECTRICIAN: 'Electrician',
  MECHANIC: 'Mechanic',
};

// ==================== AUTH ====================

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatarColor: string;
  avatarUrl?: string;
  allowedSections: SidebarSection[];
  password?: string;
  createdAt?: string;
  /** Custom notification email (editable). For @rae-pm.local users, can be any real email. */
  notificationEmail?: string;
  /** Phone number with country code (e.g. +1 (555) 123-4567) */
  notificationPhone?: string;
  updatedAt?: string;
}

// ==================== TASK STATUS (State Machine) ====================

export type TaskStatus =
  | 'ASSIGNED'
  | 'STARTED'
  | 'IN_PROGRESS'
  | 'IN_REVIEW'
  | 'PAUSED'
  | 'FINISHED'
  | 'ISSUES';

export const TaskStatusLabels: Record<TaskStatus, string> = {
  ASSIGNED: 'Assigned',
  STARTED: 'Started',
  IN_PROGRESS: 'In Progress',
  IN_REVIEW: 'In Review',
  PAUSED: 'Paused',
  FINISHED: 'Finished',
  ISSUES: 'Issues',
};

export const TaskStatusColors: Record<TaskStatus, string> = {
  ASSIGNED: '#6b7280',
  STARTED: '#3b82f6',
  IN_PROGRESS: '#f59e0b',
  IN_REVIEW: '#06b6d4',
  PAUSED: '#a78bfa',
  FINISHED: '#22c55e',
  ISSUES: '#ef4444',
};

/**
 * Progreso que representa cada estado. Sustituye al cálculo binario
 * anterior (solo FINISHED contaba), que hacía saltar las tareas de 0 a 100.
 */
export const TaskStatusProgress: Record<TaskStatus, number> = {
  ASSIGNED: 15,
  STARTED: 25,
  IN_PROGRESS: 50,
  IN_REVIEW: 80,
  FINISHED: 100,
  // PAUSED e ISSUES conservan el avance alcanzado; se resuelven en
  // getTaskProgress() a partir del historial, no con este valor.
  PAUSED: 50,
  ISSUES: 50,
};

// Valid state transitions.
// La revisión es un camino ADICIONAL: IN_PROGRESS -> FINISHED se conserva
// para no alterar la operación que el equipo ya tiene en marcha.
export const VALID_TRANSITIONS: Record<TaskStatus, TaskStatus[]> = {
  ASSIGNED: ['STARTED'],
  STARTED: ['IN_PROGRESS', 'PAUSED', 'ISSUES'],
  IN_PROGRESS: ['FINISHED', 'IN_REVIEW', 'PAUSED', 'ISSUES'],
  IN_REVIEW: ['FINISHED', 'IN_PROGRESS', 'ISSUES'],
  PAUSED: ['IN_PROGRESS', 'ISSUES'],
  FINISHED: [],
  ISSUES: ['IN_PROGRESS'],
};

/** Solo el supervisor (PM) abre y resuelve revisiones; cerrar una tarea sigue abierto a todos. */
export const SUPERVISOR_ONLY_STATUSES: TaskStatus[] = ['IN_REVIEW'];

// ==================== STATUS HISTORY ====================

export interface StatusHistoryEntry {
  id: string;
  subElementId?: string;
  field: string;
  oldValue: string;
  newValue: string;
  timestamp: string;
  changedBy: string;
  changedByName: string;
  comment?: string;
  attachmentIds?: string[];
}

// ==================== PRIORITY ====================

export type Priority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export const PriorityLabels: Record<Priority, string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  CRITICAL: 'Critical',
};

export const PriorityColors: Record<Priority, string> = {
  LOW: '#6b7280',
  MEDIUM: '#3b82f6',
  HIGH: '#f59e0b',
  CRITICAL: '#ef4444',
};

// ==================== HIERARCHICAL PROJECT STRUCTURE ====================

/** Level 1 — Macro container (physical/logical area) */
export interface Zone {
  id: string;
  name: string;
  code: string;
  description: string;
  projectId: string;
  sections: Section[];
  createdAt: string;
  updatedAt: string;
}

/** Level 2 — Functional category within a zone */
export interface Section {
  id: string;
  name: string;
  code: string;
  description: string;
  zoneId: string;
  elements: Element[];
}

/** Level 3 — Specific component or board */
export interface Element {
  id: string;
  name: string;
  code: string;
  description: string;
  sectionId: string;
  subElements: SubElement[];
}

/** Level 4 — Atomic work unit (task) */
export interface SubElement {
  id: string;
  name: string;
  code: string;
  description: string;
  elementId: string;
  status: TaskStatus;
  priority: Priority;
  assignedTo: string[];        // User IDs
  estimatedHours: number;
  startDate: string;           // ISO date string, e.g. '2026-03-01'
  endDate: string;             // ISO date string, e.g. '2026-03-15'
  timeEntries: TimeEntry[];
  comments: TaskComment[];
  attachments: TaskFile[];
  statusHistory: StatusHistoryEntry[];
  createdAt: string;
  updatedAt: string;
  /** Trabajo fuera del alcance original, pedido por el cliente en sitio. */
  isExtraWork?: boolean;
  extraWorkReason?: string;
  extraWorkBy?: string;
  extraWorkAt?: string;
  /** Tarea que originó este trabajo extra, si aplica. */
  originTaskId?: string;
}

// ==================== TIME TRACKING ====================

export interface TimeEntry {
  id: string;
  subElementId: string;
  userId: string;
  startTime: string;
  endTime: string | null;
  isPaused: boolean;
  pausedDuration: number;      // minutes of accumulated paused time
  notes: string;
}

// ==================== TASK FILES & COMMENTS ====================

export type TaskFileType = 'work_photo' | 'document' | 'status_photo' | 'comment_photo';

export interface TaskFile {
  id: string;
  subElementId: string;
  commentId?: string;
  fileType: TaskFileType;
  fileName: string;
  storagePath: string;
  mimeType: string;
  fileSize: number;
  uploadedBy: string;
  createdAt: string;
  publicUrl?: string;
}

export interface TaskComment {
  id: string;
  subElementId?: string;
  userId: string;
  userName: string;
  content: string;
  createdAt: string;
  attachments: TaskFile[];
  /** Marca de edición; ausente = nunca editado. */
  editedAt?: string;
}

// ==================== PROJECT ====================

export type ProjectStatus = 'PLANNING' | 'ACTIVE' | 'ON_HOLD' | 'COMPLETED';

export const ProjectStatusLabels: Record<ProjectStatus, string> = {
  PLANNING: 'Planning',
  ACTIVE: 'Active',
  ON_HOLD: 'On Hold',
  COMPLETED: 'Completed',
};

export const ProjectStatusColors: Record<ProjectStatus, string> = {
  PLANNING: '#a78bfa',
  ACTIVE: '#3b82f6',
  ON_HOLD: '#f59e0b',
  COMPLETED: '#22c55e',
};

export interface Project {
  id: string;
  name: string;
  code: string;
  description: string;
  client: string;
  status: ProjectStatus;
  startDate: string;
  endDate: string;
  zones: Zone[];
  teamMembers: string[];       // User IDs
  createdAt: string;
  updatedAt: string;
}

// ==================== ANALYTICS TYPES ====================

export interface ProjectAnalytics {
  totalTasks: number;
  completedTasks: number;
  inProgressTasks: number;
  issueTasks: number;
  assignedTasks: number;
  startedTasks: number;
  /** % de tareas terminadas — la métrica histórica, sin cambios. */
  progressPercentage: number;
  /** Avance ponderado por estado; cuenta también el trabajo en curso. */
  weightedProgressPercentage: number;
  totalEstimatedHours: number;
  totalActualHours: number;
  hoursVariance: number;
}

// ==================== ALERTS / NOTIFICATIONS ====================

export type AlertType =
  | 'LATE_COMPLETION'  // task finished after due date
  | 'ISSUE_REPORTED'  // task changed to ISSUES status
  | 'TASK_OVERDUE'    // task past due date, not finished
  | 'TASK_DUE_SOON'   // task due within 3 days
  | 'TASK_ASSIGNED'   // task assigned to a user
  | 'NEW_COMMENT';    // comment added to a task

export interface ProjectAlert {
  id: string;
  type: AlertType;
  projectId: string;
  subElementId: string;
  taskCode: string;
  taskName: string;
  assignedTo: string[];
  message: string;
  createdAt: string;
  isRead: boolean;
  endDate?: string;
  completedAt?: string;
}

// ==================== GANTT CHART TYPES ====================

export interface GanttRow {
  id: string;
  code: string;
  name: string;
  type: 'zone' | 'section' | 'element' | 'task';
  level: number;
  startDate?: string;
  endDate?: string;
  status?: TaskStatus;
  assignedTo?: string[];
  children?: GanttRow[];
}

export interface GanttChartProps {
  rows: GanttRow[];
  projectStartDate: string;
  projectEndDate: string;
  onTaskClick?: (taskId: string) => void;
  maxRows?: number;
}

export interface FlatRow {
  row: GanttRow;
  spanStart?: string;
  spanEnd?: string;
  childTaskCount: number;
}

export interface GanttExportConfig {
  rows: GanttRow[];
  projectStartDate: string;
  projectEndDate: string;
  statusFilter: TaskStatus | 'ALL';
  getUserName: (id: string) => string;
}

export interface FlatExportRow {
  row: GanttRow;
  spanStart?: string;
  spanEnd?: string;
  childTaskCount: number;
}

// ==================== VIEW / UI TYPES ====================

export type Theme = 'dark' | 'light';

export type ViewMode = 'list' | 'calendar' | 'kanban';

// ==================== TASK DETAIL TYPES ====================

export interface StatusModalState {
  isOpen: boolean;
  newStatus: TaskStatus | null;
}

export type FeedItem =
  | { type: 'comment'; id: string; timestamp: string; userName: string; content: string; attachments?: TaskFile[]; userId?: string; editedAt?: string }
  | { type: 'status'; id: string; timestamp: string; changedByName: string; field: string; oldValue: string; newValue: string; comment?: string; attachmentIds?: string[] };

// ==================== WORKSPACE TYPES ====================

/**
 * Alta rápida de tarea. Solo `code` y `name` son obligatorios: el resto son
 * anexos opcionales que, si se omiten, dan el mismo resultado de siempre.
 */
export interface QuickTaskInput {
  code: string;
  name: string;
  priority?: Priority;
  description?: string;
  assignedTo?: string[];
  estimatedHours?: number;
  startDate?: string;
  endDate?: string;
  /** Destino en el árbol. Sin esto, la tarea va a "General" como hasta ahora. */
  elementId?: string;
}

export interface EditingState {
  id: string;
  code: string;
  name: string;
}

export interface AddingState {
  parentId: string;
  type: 'zone' | 'section' | 'element' | 'subElement';
  code: string;
  name: string;
}

// ==================== MY TASKS TYPES ====================

export interface WeekGroup {
  weekKey: string;
  label: string;
  days: Map<string, SubElement[]>;
}

// ==================== CAPACITY PLANNING ====================
// Man-hour / staffing planner (port of the "Man-Hour Calculator" spreadsheet).
// Plans capacity SUPPLY (people per discipline per week) — complements the app's
// demand tracking (task estimatedHours / actualHours).

// Disciplines map to the app's UserRole values so staffing lines up with real roles.
export type PlanDiscipline = Exclude<UserRole, 'PROJECT_MANAGER'> | 'PROJECT_MANAGER';

export const PLAN_DISCIPLINES: { key: PlanDiscipline; label: string }[] = [
  { key: 'PROJECT_MANAGER', label: 'Management' },
  { key: 'MECHANIC',        label: 'Mechanical' },
  { key: 'ELECTRICIAN',     label: 'Electrical' },
  { key: 'TECHNICIAN',      label: 'Technician' },
  { key: 'HELPER',          label: 'Support' },
];

export interface CapacityPlanParams {
  hoursPerShift: number;    // e.g. 12
  daysPerWeek: number;      // e.g. 6
  activeShifts: number;     // 1 = base plan, 2 = recovery
  shiftMultiplier: number;  // applied to man-hours
}

export interface CapacityPlan {
  projectId: string;
  params: CapacityPlanParams;
  weekCount: number;                          // manual or derived from project dates
  // headcount per discipline, indexed by week (length === weekCount)
  staffing: Record<PlanDiscipline, number[]>;
  // Optional per-project renaming of the discipline labels (key omitted = default).
  disciplineLabels?: Partial<Record<PlanDiscipline, string>>;
  updatedAt: string;
}
